/**
 * P4 — the "very easy" per-brand KPI/targets screen (`/client-reports/targets`).
 *
 * One screen per brand: a brand switcher + year selector, a headline Year goal,
 * a 12-month budget row (spend caps, with a "fill right" helper), and a
 * freeform metric-target table fed by the metrics vocabulary with one-click
 * metric packs. Everything is optional; Save diffs the working draft against
 * the loaded targets and issues per-row upserts (POST) + deletes.
 *
 * Gated on `portal.kpis.manage`. The backend additionally enforces the caller's
 * brand access on every shop-scoped route.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { useCan } from '../../../lib/rbac';
import { setMessage } from '../../../store/reducers/appSlice';
import { HOME_ROUTE } from '../../../lib/constants/route';
import type { RootState } from '../../../store/store';
import ClientReportsNav from '../ClientReportsNav';
import {
  listMetrics,
  createMetric,
  listKpiTargets,
  upsertKpiTarget,
  deleteKpiTarget,
  type MetricDefinition,
  type MetricDirection,
  type MetricUnit,
  type KpiTarget,
  type TargetPlatform,
  type TargetType,
} from '../../../lib/api/client-reports/kpi-targets-api';
import {
  METRIC_PACKS,
  applyMetricPack,
  buildPeriodOptions,
  computeTargetMutations,
  defaultTargetTypeForDirection,
  isValidMetricKey,
  metricKeyFromLabel,
  monthLabel,
  monthPeriodStart,
  nextRowKey,
  periodValue,
  type KpiDraftRow,
} from '../kpi-helpers';
import '../client-reports.scss';

const PLATFORMS: TargetPlatform[] = [
  'blended',
  'meta',
  'google',
  'tiktok',
  'linkedin',
  'ga4',
];
const PLATFORM_LABEL: Record<string, string> = {
  blended: 'Blended',
  meta: 'Meta',
  google: 'Google',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  ga4: 'GA4',
};
const TARGET_TYPES: Array<{ value: TargetType; label: string }> = [
  { value: 'target', label: 'Target' },
  { value: 'cap', label: 'Cap (max)' },
  { value: 'floor', label: 'Floor (min)' },
];

const UNITS: Array<{ value: MetricUnit; label: string }> = [
  { value: 'currency', label: 'Currency (e.g. €25)' },
  { value: 'ratio', label: 'Ratio (e.g. 4.2×)' },
  { value: 'percent', label: 'Percent (e.g. 2.5%)' },
  { value: 'count', label: 'Count (e.g. 1,200)' },
];
const DIRECTIONS: Array<{ value: MetricDirection; label: string }> = [
  { value: 'higher_better', label: 'Higher is better' },
  { value: 'lower_better', label: 'Lower is better' },
];

// Sentinel <option> that opens the custom-metric form instead of picking one.
const ADD_CUSTOM = '__custom__';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_CHOICES = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];
const DEFAULT_CURRENCY = 'EUR';

// Which drafted rows are "special" (rendered by dedicated cards, not the table).
//
// Keyed on the row IDENTITY assigned by rebuildRows, NOT on the row's shape: a
// freshly added higher-is-better metric has the exact same shape as the year
// goal (year + target + blended), so a shape test both filtered it out of the
// table and lost the find() for the real year goal — the row rendered nowhere.
const isYearGoal = (r: KpiDraftRow) => r.rowKey.startsWith('yeargoal-');
const isMonthlyBudget = (r: KpiDraftRow) => r.rowKey.startsWith('month');

const ReportKpiTargets = () => {
  const dispatch = useDispatch();
  const canManage = useCan('portal.kpis.manage');

  // Operate on the GLOBAL active brand (switched from the bottom-left sidebar
  // button) instead of an in-page brand selector.
  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops || []
  );
  const activeShopName = useMemo(
    () =>
      allShops.find((sh: any) => sh?.slug === activeShopSlug)?.name ||
      activeShopSlug,
    [allShops, activeShopSlug]
  );

  const [year, setYear] = useState(CURRENT_YEAR);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);

  const [metrics, setMetrics] = useState<MetricDefinition[]>([]);
  const [originalTargets, setOriginalTargets] = useState<KpiTarget[]>([]);
  const [rows, setRows] = useState<KpiDraftRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addMetric, setAddMetric] = useState('');

  // Custom-metric composer (org-scoped vocabulary; POST /metrics).
  const [customOpen, setCustomOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customKey, setCustomKey] = useState('');
  const [customKeyEdited, setCustomKeyEdited] = useState(false);
  const [customUnit, setCustomUnit] = useState<MetricUnit>('currency');
  const [customDirection, setCustomDirection] =
    useState<MetricDirection>('higher_better');
  const [creating, setCreating] = useState(false);

  const metricByKey = useMemo(() => {
    const m = new Map<string, MetricDefinition>();
    metrics.forEach((d) => m.set(d.key, d));
    return m;
  }, [metrics]);

  const directionOf = useCallback(
    (key: string) => metricByKey.get(key)?.direction,
    [metricByKey]
  );
  const labelOf = useCallback(
    (key: string) => metricByKey.get(key)?.label || key,
    [metricByKey]
  );
  const unitOf = useCallback(
    (key: string) => metricByKey.get(key)?.unit || '',
    [metricByKey]
  );

  const periodOptions = useMemo(() => buildPeriodOptions(year), [year]);

  // Load the metrics vocabulary once.
  useEffect(() => {
    let cancelled = false;
    listMetrics()
      .then((m) => {
        if (!cancelled) setMetrics(m);
      })
      .catch((err: any) => {
        dispatch(
          setMessage({
            message: err?.message || 'Failed to load the metrics list.',
            messageType: 'error',
          })
        );
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // (Re)build the working draft from the loaded targets for the chosen year.
  const rebuildRows = useCallback(
    (targets: KpiTarget[], forYear: number) => {
      const thisYear = targets.filter((t) =>
        (t.period_start || '').startsWith(`${forYear}-`)
      );

      // Seed currency from any existing target.
      const seededCurrency =
        thisYear.find((t) => t.currency)?.currency || DEFAULT_CURRENCY;
      setCurrency(seededCurrency);

      const draft: KpiDraftRow[] = [];

      // 1) Year goal — first annual blended target.
      const yg = thisYear.find(
        (t) =>
          t.period_type === 'year' &&
          t.target_type === 'target' &&
          t.platform === 'blended'
      );
      draft.push({
        rowKey: nextRowKey('yeargoal'),
        serverId: yg?.id ?? null,
        metric_key: yg?.metric_key ?? 'revenue',
        platform: 'blended',
        period_type: 'year',
        period_start: `${forYear}-01-01`,
        value: yg ? String(yg.target_value) : '',
        target_type: 'target',
        currency: yg?.currency || seededCurrency,
        notes: yg?.notes || '',
      });

      // 2) Monthly budget — 12 spend caps.
      for (let m = 0; m < 12; m += 1) {
        const ps = monthPeriodStart(forYear, m);
        const existing = thisYear.find(
          (t) =>
            t.metric_key === 'spend' &&
            t.period_type === 'month' &&
            t.target_type === 'cap' &&
            t.platform === 'blended' &&
            t.period_start === ps
        );
        draft.push({
          rowKey: nextRowKey(`month${m}`),
          serverId: existing?.id ?? null,
          metric_key: 'spend',
          platform: 'blended',
          period_type: 'month',
          period_start: ps,
          value: existing ? String(existing.target_value) : '',
          target_type: 'cap',
          currency: existing?.currency || seededCurrency,
          notes: existing?.notes || '',
        });
      }

      // 3) Everything else → the freeform table.
      const specialIds = new Set(
        draft.filter((r) => r.serverId).map((r) => r.serverId as string)
      );
      thisYear.forEach((t) => {
        if (specialIds.has(t.id)) return;
        draft.push({
          rowKey: nextRowKey('tbl'),
          serverId: t.id,
          metric_key: t.metric_key,
          platform: t.platform,
          period_type: t.period_type,
          period_start: t.period_start,
          value: String(t.target_value),
          target_type: t.target_type,
          currency: t.currency || seededCurrency,
          notes: t.notes || '',
        });
      });

      setRows(draft);
    },
    []
  );

  const loadTargets = useCallback(
    async (slug: string, forYear: number) => {
      if (!slug) return;
      setLoading(true);
      try {
        const targets = await listKpiTargets(slug);
        setOriginalTargets(targets);
        rebuildRows(targets, forYear);
      } catch (err: any) {
        const msg =
          err?.status === 403
            ? 'You do not have access to that brand.'
            : err?.message || 'Failed to load KPI targets.';
        dispatch(setMessage({ message: msg, messageType: 'error' }));
        setOriginalTargets([]);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, rebuildRows]
  );

  useEffect(() => {
    if (activeShopSlug) loadTargets(activeShopSlug, year);
  }, [activeShopSlug, year, loadTargets]);

  // ---- row mutation helpers ----

  const patchRow = (rowKey: string, patch: Partial<KpiDraftRow>) =>
    setRows((prev) =>
      prev.map((r) => (r.rowKey === rowKey ? { ...r, ...patch } : r))
    );

  const removeRow = (rowKey: string) =>
    setRows((prev) => prev.filter((r) => r.rowKey !== rowKey));

  const yearGoalRow = rows.find(isYearGoal);
  const monthlyRows = rows.filter(isMonthlyBudget);
  const tableRows = rows.filter((r) => !isYearGoal(r) && !isMonthlyBudget(r));

  // "Fill right": copy the first non-empty month value into all later empties.
  const fillRightBudget = () => {
    setRows((prev) => {
      const months = prev.filter(isMonthlyBudget);
      const firstVal = months.find((m) => m.value.trim() !== '')?.value ?? '';
      if (firstVal === '') return prev;
      let started = false;
      return prev.map((r) => {
        if (!isMonthlyBudget(r)) return r;
        if (r.value.trim() !== '') {
          started = true;
          return r;
        }
        if (started) return { ...r, value: firstVal };
        return r;
      });
    });
  };

  const appendMetricRow = useCallback(
    (metricKey: string, direction?: string) => {
      setRows((prev) => [
        ...prev,
        {
          rowKey: nextRowKey('tbl'),
          serverId: null,
          metric_key: metricKey,
          platform: 'blended',
          period_type: 'year',
          period_start: `${year}-01-01`,
          value: '',
          target_type: defaultTargetTypeForDirection(direction),
          currency,
          notes: '',
        },
      ]);
    },
    [year, currency]
  );

  const handleAddMetric = () => {
    if (!addMetric) return;
    appendMetricRow(addMetric, metricByKey.get(addMetric)?.direction);
    setAddMetric('');
  };

  const resetCustom = () => {
    setCustomOpen(false);
    setCustomLabel('');
    setCustomKey('');
    setCustomKeyEdited(false);
    setCustomUnit('currency');
    setCustomDirection('higher_better');
  };

  // The key the user will actually get: auto-derived from the label until they
  // override it by hand.
  const effectiveCustomKey = customKeyEdited
    ? customKey
    : metricKeyFromLabel(customLabel);

  const customKeyTaken = metricByKey.has(effectiveCustomKey);
  const canCreateCustom =
    customLabel.trim() !== '' &&
    effectiveCustomKey !== '' &&
    isValidMetricKey(effectiveCustomKey) &&
    !customKeyTaken &&
    !creating;

  const handleCreateCustomMetric = async () => {
    if (!canCreateCustom) return;
    setCreating(true);
    try {
      const def = await createMetric({
        key: effectiveCustomKey,
        label: customLabel.trim(),
        unit: customUnit,
        direction: customDirection,
      });
      // Add to the local vocabulary so the row renders its label/unit at once.
      setMetrics((prev) => [...prev, def]);
      appendMetricRow(def.key, def.direction);
      resetCustom();
      dispatch(
        setMessage({
          message: `Added "${def.label}". Set a value, then Save targets.`,
          messageType: 'success',
        })
      );
    } catch (err: any) {
      const validation = err?.validation;
      const firstError =
        validation && typeof validation === 'object'
          ? (Object.values(validation)[0] as any)?.[0]
          : null;
      dispatch(
        setMessage({
          message: firstError || err?.message || 'Failed to create the metric.',
          messageType: 'error',
        })
      );
    } finally {
      setCreating(false);
    }
  };

  const handlePack = (packKey: string) => {
    const pack = METRIC_PACKS.find((p) => p.key === packKey);
    if (!pack) return;
    setRows((prev) =>
      applyMetricPack(prev, pack, {
        year,
        currency,
        directionOf,
        platform: 'blended',
      })
    );
  };

  // ---- dirty tracking + save ----

  const originalThisYear = useMemo(
    () =>
      originalTargets.filter((t) =>
        (t.period_start || '').startsWith(`${year}-`)
      ),
    [originalTargets, year]
  );

  const mutations = useMemo(
    () => computeTargetMutations(originalThisYear, rows),
    [originalThisYear, rows]
  );
  const isDirty = mutations.upserts.length > 0 || mutations.deletes.length > 0;

  const handleSave = async () => {
    if (!activeShopSlug || !isDirty || saving) return;
    setSaving(true);
    try {
      // Deletes first so a moved tuple frees its unique slot before the upsert.
      for (const id of mutations.deletes) {
        await deleteKpiTarget(activeShopSlug, id);
      }
      for (const body of mutations.upserts) {
        await upsertKpiTarget(activeShopSlug, body);
      }
      dispatch(
        setMessage({ message: 'KPI targets saved.', messageType: 'success' })
      );
      await loadTargets(activeShopSlug, year);
    } catch (err: any) {
      dispatch(
        setMessage({
          message: err?.message || 'Failed to save KPI targets.',
          messageType: 'error',
        })
      );
      // Reload to resync with whatever landed.
      await loadTargets(activeShopSlug, year);
    } finally {
      setSaving(false);
    }
  };

  // Metrics not already in the table (avoid obvious dupes in the add combobox).
  const addableMetrics = useMemo(() => {
    const inTable = new Set(tableRows.map((r) => r.metric_key));
    return metrics.filter((m) => !inTable.has(m.key));
  }, [metrics, tableRows]);

  if (!canManage) {
    return (
      <SubPageInnerContentHolder title='KPI targets' backBtnLink={HOME_ROUTE}>
        <div className='client-reports__empty'>
          You don't have permission to manage KPI targets. Ask an owner or
          manager for the "manage KPIs" capability.
        </div>
      </SubPageInnerContentHolder>
    );
  }

  return (
    <SubPageInnerContentHolder title='KPI targets' backBtnLink={HOME_ROUTE}>
      <ClientReportsNav />

      <div className='report-kpi'>
        {/* ---- Brand + year header ---- */}
        <div className='report-kpi__header'>
          <div className='report-kpi__field'>
            <label className='report-kpi__label'>Active brand</label>
            <div className='client-reports__active-brand'>
              <span className='client-reports__active-brand-name'>
                {activeShopName || '—'}
              </span>
              <span className='client-reports__active-brand-hint'>
                Switch brand from the button in the bottom-left.
              </span>
            </div>
          </div>
          <div className='report-kpi__field'>
            <label className='report-kpi__label' htmlFor='kpi-year'>
              Year
            </label>
            <select
              id='kpi-year'
              className='report-kpi__select'
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {YEAR_CHOICES.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className='report-kpi__field'>
            <label className='report-kpi__label' htmlFor='kpi-currency'>
              Currency
            </label>
            <input
              id='kpi-currency'
              className='report-kpi__input report-kpi__input--currency'
              value={currency}
              maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            />
          </div>
          <div className='report-kpi__header-save'>
            <MainSquareButton
              type='button'
              value={saving ? 'Saving…' : 'Save targets'}
              buttonType='success'
              normal
              loading={saving}
              disabled={!isDirty || saving || loading}
              onClick={handleSave}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <LoadingSpinner customLoadingMsg='Loading targets…' />
          </div>
        ) : !activeShopSlug ? (
          <div className='client-reports__empty'>
            No active brand. Pick one from the button in the bottom-left.
          </div>
        ) : (
          <>
            {/* ---- Year goal ---- */}
            {yearGoalRow && (
              <section className='report-kpi__card'>
                <h3 className='report-kpi__card-title'>Year goal</h3>
                <p className='report-kpi__card-hint'>
                  The single headline goal for {year} (blended across all
                  channels). Reports quote this as the north-star target.
                </p>
                <div className='report-kpi__yeargoal'>
                  <div className='report-kpi__field'>
                    <label className='report-kpi__label'>Metric</label>
                    <select
                      className='report-kpi__select'
                      value={yearGoalRow.metric_key}
                      onChange={(e) =>
                        patchRow(yearGoalRow.rowKey, {
                          metric_key: e.target.value,
                        })
                      }
                    >
                      {metrics.map((m) => (
                        <option key={m.key} value={m.key}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='report-kpi__field'>
                    <label className='report-kpi__label'>Target value</label>
                    <input
                      className='report-kpi__input'
                      inputMode='decimal'
                      placeholder='e.g. 500000'
                      value={yearGoalRow.value}
                      onChange={(e) =>
                        patchRow(yearGoalRow.rowKey, { value: e.target.value })
                      }
                    />
                  </div>
                  <div className='report-kpi__unit'>{unitOf(yearGoalRow.metric_key)}</div>
                </div>
              </section>
            )}

            {/* ---- Monthly budget ---- */}
            <section className='report-kpi__card'>
              <div className='report-kpi__card-head'>
                <div>
                  <h3 className='report-kpi__card-title'>
                    Monthly budget ({currency})
                  </h3>
                  <p className='report-kpi__card-hint'>
                    Planned ad spend cap per month. Type the first month then
                    "Fill right" to copy it forward.
                  </p>
                </div>
                <button
                  type='button'
                  className='report-kpi__linkbtn'
                  onClick={fillRightBudget}
                >
                  Fill right →
                </button>
              </div>
              <div className='report-kpi__months'>
                {monthlyRows.map((r, i) => (
                  <div key={r.rowKey} className='report-kpi__month'>
                    <span className='report-kpi__month-name'>
                      {monthLabel(i)}
                    </span>
                    <input
                      className='report-kpi__month-input'
                      inputMode='decimal'
                      value={r.value}
                      onChange={(e) =>
                        patchRow(r.rowKey, { value: e.target.value })
                      }
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* ---- Metric-target table ---- */}
            <section className='report-kpi__card'>
              <div className='report-kpi__card-head'>
                <div>
                  <h3 className='report-kpi__card-title'>Metric targets</h3>
                  <p className='report-kpi__card-hint'>
                    Specific targets (e.g. Meta CPA ≤ €25). Add one metric at a
                    time, or start from a pack.
                  </p>
                </div>
              </div>

              <div className='report-kpi__packs'>
                <span className='report-kpi__packs-label'>Quick packs:</span>
                {METRIC_PACKS.map((p) => (
                  <button
                    key={p.key}
                    type='button'
                    className='report-kpi__pack-btn'
                    onClick={() => handlePack(p.key)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {tableRows.length === 0 ? (
                <div className='report-kpi__table-empty'>
                  No metric targets yet — add one below or pick a pack.
                </div>
              ) : (
                <div className='report-kpi__table' role='table'>
                  <div className='report-kpi__trow report-kpi__trow--head' role='row'>
                    <span>Metric</span>
                    <span>Platform</span>
                    <span>Period</span>
                    <span>Type</span>
                    <span>Value</span>
                    <span />
                  </div>
                  {tableRows.map((r) => (
                    <div key={r.rowKey} className='report-kpi__trow' role='row'>
                      <span className='report-kpi__metric-cell'>
                        <span className='report-kpi__metric-name'>
                          {labelOf(r.metric_key)}
                        </span>
                        {unitOf(r.metric_key) && (
                          <span className='report-kpi__metric-unit'>
                            {unitOf(r.metric_key)}
                          </span>
                        )}
                      </span>
                      <select
                        className='report-kpi__cell-select'
                        value={r.platform}
                        onChange={(e) =>
                          patchRow(r.rowKey, {
                            platform: e.target.value as TargetPlatform,
                          })
                        }
                      >
                        {PLATFORMS.map((p) => (
                          <option key={p} value={p}>
                            {PLATFORM_LABEL[p] || p}
                          </option>
                        ))}
                      </select>
                      <select
                        className='report-kpi__cell-select'
                        value={periodValue(r.period_type, r.period_start)}
                        onChange={(e) => {
                          const opt = periodOptions.find(
                            (o) => o.value === e.target.value
                          );
                          if (opt)
                            patchRow(r.rowKey, {
                              period_type: opt.period_type,
                              period_start: opt.period_start,
                            });
                        }}
                      >
                        {periodOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <select
                        className='report-kpi__cell-select'
                        value={r.target_type}
                        onChange={(e) =>
                          patchRow(r.rowKey, {
                            target_type: e.target.value as TargetType,
                          })
                        }
                      >
                        {TARGET_TYPES.map((tt) => (
                          <option key={tt.value} value={tt.value}>
                            {tt.label}
                          </option>
                        ))}
                      </select>
                      <input
                        className='report-kpi__cell-input'
                        inputMode='decimal'
                        placeholder='value'
                        value={r.value}
                        onChange={(e) =>
                          patchRow(r.rowKey, { value: e.target.value })
                        }
                      />
                      <button
                        type='button'
                        className='report-kpi__row-remove'
                        title='Remove target'
                        onClick={() => removeRow(r.rowKey)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className='report-kpi__add'>
                <select
                  className='report-kpi__select'
                  value={addMetric}
                  onChange={(e) => {
                    if (e.target.value === ADD_CUSTOM) {
                      setAddMetric('');
                      setCustomOpen(true);
                      return;
                    }
                    setAddMetric(e.target.value);
                  }}
                >
                  <option value=''>Add a metric…</option>
                  {addableMetrics.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                  <option value={ADD_CUSTOM}>+ Custom metric…</option>
                </select>
                <MainSquareButton
                  type='button'
                  value='Add'
                  buttonType='light'
                  normal
                  disabled={!addMetric}
                  onClick={handleAddMetric}
                />
              </div>

              {customOpen && (
                <div className='report-kpi__custom'>
                  <p className='report-kpi__custom-hint'>
                    Track something the list doesn't cover. Custom metrics belong
                    to your agency, are reusable across all brands, and are
                    readable by the assistant when it writes reports.
                  </p>
                  <div className='report-kpi__custom-grid'>
                    <div className='report-kpi__field'>
                      <label
                        className='report-kpi__label'
                        htmlFor='custom-metric-label'
                      >
                        Name
                      </label>
                      <input
                        id='custom-metric-label'
                        className='report-kpi__input'
                        placeholder='e.g. Contribution margin'
                        value={customLabel}
                        onChange={(e) => setCustomLabel(e.target.value)}
                      />
                    </div>
                    <div className='report-kpi__field'>
                      <label
                        className='report-kpi__label'
                        htmlFor='custom-metric-key'
                      >
                        Key
                      </label>
                      <input
                        id='custom-metric-key'
                        className='report-kpi__input'
                        placeholder='contribution_margin'
                        value={effectiveCustomKey}
                        onChange={(e) => {
                          setCustomKeyEdited(true);
                          setCustomKey(e.target.value);
                        }}
                      />
                    </div>
                    <div className='report-kpi__field'>
                      <label
                        className='report-kpi__label'
                        htmlFor='custom-metric-unit'
                      >
                        Unit
                      </label>
                      <select
                        id='custom-metric-unit'
                        className='report-kpi__select'
                        value={customUnit}
                        onChange={(e) =>
                          setCustomUnit(e.target.value as MetricUnit)
                        }
                      >
                        {UNITS.map((u) => (
                          <option key={u.value} value={u.value}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='report-kpi__field'>
                      <label
                        className='report-kpi__label'
                        htmlFor='custom-metric-direction'
                      >
                        Good is
                      </label>
                      <select
                        id='custom-metric-direction'
                        className='report-kpi__select'
                        value={customDirection}
                        onChange={(e) =>
                          setCustomDirection(e.target.value as MetricDirection)
                        }
                      >
                        {DIRECTIONS.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {customLabel.trim() !== '' &&
                    effectiveCustomKey !== '' &&
                    !isValidMetricKey(effectiveCustomKey) && (
                      <p className='report-kpi__custom-error'>
                        The key may only contain lowercase letters, numbers and
                        underscores.
                      </p>
                    )}
                  {customKeyTaken && (
                    <p className='report-kpi__custom-error'>
                      "{effectiveCustomKey}" already exists — pick it from the
                      list above instead.
                    </p>
                  )}

                  <div className='report-kpi__custom-actions'>
                    <MainSquareButton
                      type='button'
                      value={creating ? 'Adding…' : 'Add metric'}
                      buttonType='success'
                      normal
                      loading={creating}
                      disabled={!canCreateCustom}
                      onClick={handleCreateCustomMetric}
                    />
                    <button
                      type='button'
                      className='report-kpi__linkbtn'
                      onClick={resetCustom}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </section>

            <p className='report-kpi__footnote'>
              Everything here is optional — a brand with just a monthly budget is
              valid. The assistant compiles whatever exists into each report's
              KPI summary.
            </p>
          </>
        )}
      </div>
    </SubPageInnerContentHolder>
  );
};

export default ReportKpiTargets;
